"use client";

import { useEffect, useMemo, useState } from "react";
import { Box, Button, Link } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import type { SponsorCampaign } from "../campaignTypes";
import { PORTAL_MAX_WIDTH, portalFont, portalShellSx } from "../partnerTheme";
import { dkStadiumConfig } from "../campaigns/draft-kings-stadium";
import DraftKingsAboutTab from "./DraftKingsAboutTab";
import DraftKingsStadiumTourTab from "./DraftKingsStadiumTourTab";
import DraftKingsTapingsTab from "./DraftKingsTapingsTab";

const ACCENT = dkStadiumConfig.accentColor;
const LINE = "rgba(255,255,255,0.12)";
const MUTED = "rgba(244,245,247,0.62)";

export type DkStadiumTab = "about" | "stadium" | "tapings";

const TABS: { id: DkStadiumTab; label: string }[] = [
  { id: "about", label: "About Us" },
  { id: "stadium", label: "Stadium Tour" },
  { id: "tapings", label: "Comedy Tapings" },
];

function parseTab(raw: string | null): DkStadiumTab {
  if (raw === "stadium" || raw === "tapings" || raw === "about") return raw;
  return "about";
}

/**
 * Anonymous Stadium Tour ABM portal: About Us, Stadium Tour, Comedy Tapings.
 * Isolated route. Does not modify Amex/BILT/Skrewball portals.
 */
export default function DraftKingsStadiumPage({ campaign }: { campaign: SponsorCampaign }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = useMemo(() => parseTab(searchParams.get("tab")), [searchParams]);
  const [visible, setVisible] = useState(true);

  function openTab(next: DkStadiumTab) {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setVisible(false);
    window.requestAnimationFrame(() => setVisible(true));
  }

  useEffect(() => {
    if (!searchParams.get("tab")) {
      const params = new URLSearchParams(searchParams);
      params.set("tab", "about");
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <Box sx={{ ...portalShellSx(campaign), bgcolor: "#000" }}>
      <Header tab={tab} onTabChange={openTab} />
      <Box
        sx={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 220ms ease, transform 220ms ease",
        }}
      >
        {tab === "about" ? (
          <DraftKingsAboutTab onContinueToStadium={() => openTab("stadium")} />
        ) : null}
        {tab === "stadium" ? <DraftKingsStadiumTourTab /> : null}
        {tab === "tapings" ? <DraftKingsTapingsTab /> : null}
      </Box>
    </Box>
  );
}

function Header({
  tab,
  onTabChange,
}: {
  tab: DkStadiumTab;
  onTabChange: (tab: DkStadiumTab) => void;
}) {
  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        bgcolor: "rgba(0,0,0,0.78)",
        backdropFilter: "blur(14px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Box
        sx={{
          maxWidth: PORTAL_MAX_WIDTH,
          mx: "auto",
          px: { xs: 2.5, md: 4 },
          height: { xs: 64, md: 72 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            component="img"
            src="https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png"
            alt="ComedyUO"
            sx={{ height: { xs: 34, md: 44 }, width: "auto", objectFit: "contain" }}
          />
        </Box>

        <Box
          component="nav"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, md: 1 },
            overflowX: "auto",
            minWidth: 0,
          }}
        >
          {TABS.map((item) => {
            const active = item.id === tab;
            return (
              <Button
                key={item.id}
                onClick={() => onTabChange(item.id)}
                sx={{
                  fontFamily: portalFont,
                  fontSize: { xs: "0.62rem", md: "0.6875rem" },
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: active ? "#0a0a0a" : MUTED,
                  bgcolor: active ? ACCENT : "transparent",
                  borderRadius: "999px",
                  px: { xs: 1.25, md: 1.75 },
                  py: 0.85,
                  whiteSpace: "nowrap",
                  minWidth: "auto",
                  "&:hover": {
                    bgcolor: active ? ACCENT : "rgba(255,255,255,0.06)",
                    color: active ? "#0a0a0a" : "#fff",
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        <Link
          href={`mailto:${dkStadiumConfig.cta.email}?subject=${encodeURIComponent("ComedyUO Stadium Tour")}`}
          underline="none"
          sx={{
            display: { xs: "none", lg: "inline-flex" },
            fontFamily: portalFont,
            fontWeight: 600,
            fontSize: "0.6875rem",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "#0a0a0a",
            bgcolor: "#fff",
            borderRadius: "999px",
            px: 2,
            py: 0.9,
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
        >
          Talk with us
        </Link>
      </Box>
    </Box>
  );
}
